import { useNavigate } from "react-router-dom";
import { Job } from "~/components/build/Show/lib/types";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  nextJobFailureQueryKey,
  nextJobFailureQueryOptions,
} from "~/components/build/Show/lib/queries/nextJobFailure";
import { jobQueryOptions } from "~/components/build/Show/lib/queries/job";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";

type UseFocusOnFailureReturn = {
  hasNextFailure: boolean;
  focusOnFailure: () => void;
};

// Used as a magic value to represent the last job in the build (therefore making the 'next failure'
// the first failure in the build.
const ZERO_UUID = "00000000-0000-0000-0000-000000000000";

export const useFocusOnFailure = (): UseFocusOnFailureReturn => {
  const { build } = useBuild();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { currentLatestJobUuid, currentJobUuid } = useCurrentJobOrStep();

  const nextFailuresQuery = useQuery(
    nextJobFailureQueryOptions(build, currentLatestJobUuid || currentJobUuid || ZERO_UUID),
  );

  // Preload the next failure
  useEffect(() => {
    if (nextFailuresQuery.data) {
      // Check if the next failure is already in the cache
      const nextFailureKey = nextJobFailureQueryKey(
        build.id,
        nextFailuresQuery.data,
        build.statistics?.jobsFailedCount ?? 0,
      );
      const cacheData = queryClient.getQueryData(nextFailureKey) as Job["id"] | null;
      if (cacheData) {
        // We've got the next failure, let's get the next next one too (so we are essentially loading the next two)
        queryClient.ensureQueryData(nextJobFailureQueryOptions(build, cacheData));
        queryClient.ensureQueryData(jobQueryOptions(build, cacheData));
      } else {
        queryClient.ensureQueryData(nextJobFailureQueryOptions(build, nextFailuresQuery.data));
        queryClient.ensureQueryData(jobQueryOptions(build, nextFailuresQuery.data));
      }
    }
  }, [nextFailuresQuery.data]);

  const focusOnFailure = useCallback(async () => {
    try {
      const cursorNow = currentLatestJobUuid || currentJobUuid || ZERO_UUID;
      const nextJobId = await queryClient.ensureQueryData(
        nextJobFailureQueryOptions(build, cursorNow),
      );

      if (nextJobId) {
        navigate(resolveJobUrl(nextJobId), { state: { forceFocus: true } });
      }
    } catch (_error) {
      // Swallow errors from next-failure lookup to avoid unhandled promise rejections.
    }
  }, [build, currentLatestJobUuid, currentJobUuid, queryClient, navigate]);

  return {
    hasNextFailure: Boolean(nextFailuresQuery.data),
    focusOnFailure,
  };
};
